import { useMemo } from "react";
import { Permissions, Workspace } from "ts-interfaces";
import { allFirmIdsForUser, deriveTeamRole, isDesignerRole } from "../team";
import { useWorkspaceContext, useActiveFirmIds } from "./useWorkspaceContext";
import { workspacesEnabled } from "./featureFlag";

/**
 * The workspace whose credits should be DISPLAYED and SPENT.
 *
 * When the workspaces feature is ENABLED this is just the active workspace —
 * the user picks their workspace via the switcher and credits follow it.
 *
 * When workspaces are GATED in production (see featureFlag.ts), the rest of the
 * app is forced to Personal: no switcher, no team content scoping, new
 * boards/collections/projects stamped Personal. Credits are the deliberate
 * exception. Before the workspaces feature existed a team member always SAW and
 * SPENT their TEAM credits (pre-workspaces `useTeamMembership` pinned the first
 * membership); the production gate must not regress that — otherwise a team
 * designer sees their tiny Personal balance instead of the team's, and every
 * generation drains Personal credits.
 *
 * So when gated we pin the credit workspace to the user's first team in which
 * they hold a DESIGNER seat (designer or designer_and_admin) AND are still an
 * ACTIVE roster member. The designer seat is the only one that can spend team
 * credits — a pure `admin` manages the team but cannot spend, and the backend's
 * designer-gating in addMessageController would reject the charge anyway, so a
 * pure admin keeps their Personal wallet. The active-roster check matters
 * because a deactivated (inactive) designer keeps their `teamsDesigner` seat —
 * without it they would be shown/charged a team wallet they can no longer spend.
 * Users with no active designer seat fall through to Personal.
 *
 * Roster status is a live `teams` read (useActiveFirmIds); while it resolves we
 * pin OPTIMISTICALLY to the first designer seat (pre-workspaces behavior, no
 * flash for the common active case) and correct inactive designers to Personal
 * once it lands.
 *
 * `getActiveDesignFirmId` deliberately still resolves to null when gated, so
 * this only redirects CREDITS to the team — content stays Personal-scoped.
 */
export const useCreditWorkspace = (
  uid: string | null | undefined,
  permissions: Permissions | null | undefined,
): Workspace => {
  const { active } = useWorkspaceContext(uid, permissions);
  const enabled = workspacesEnabled();

  // Gated-mode candidate firms: those where the user holds a designer seat, in
  // permission order. Empty when enabled / no uid so useActiveFirmIds no-ops.
  const designerFirmIds = useMemo(() => {
    if (enabled || !uid) return [];
    return allFirmIdsForUser(permissions).filter((firmId) => {
      const role = deriveTeamRole(permissions, firmId);
      return !!(role && isDesignerRole(role));
    });
  }, [enabled, uid, permissions]);

  // Live active-roster subset of those firms. null while resolving.
  const activeFirmIds = useActiveFirmIds(uid, designerFirmIds);

  return useMemo<Workspace>(() => {
    // Enabled: credits follow the user's chosen workspace.
    if (enabled) return active;
    // `active` is the gated Personal workspace — the fallback for non-members,
    // pure admins, and deactivated designers.
    if (!uid || designerFirmIds.length === 0) return active;
    const pinTeam = (firmId: string): Workspace => ({
      id: `team:${firmId}`,
      kind: "team",
      firmId,
      // Non-null: designerFirmIds was filtered to designer-seat firms.
      role: deriveTeamRole(permissions, firmId)!,
    });
    // Optimistic while roster resolves: pin to the first designer seat.
    if (activeFirmIds === null) return pinTeam(designerFirmIds[0]);
    // Resolved: pin to the first designer seat the user is STILL active in;
    // a deactivated designer (seat present, roster inactive) falls to Personal.
    for (const firmId of designerFirmIds) {
      if (activeFirmIds.has(firmId)) return pinTeam(firmId);
    }
    return active;
  }, [enabled, active, uid, permissions, designerFirmIds, activeFirmIds]);
};
